import { useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, UserRound } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SidebarContent } from "./Sidebar";
import { NotificationBell } from "./NotificationBell";

const UserMenu = ({ user, onLogout }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid="user-menu-btn" className="flex items-center gap-2.5 rounded-full border border-[#e4e8e1] bg-white pl-1 pr-3 py-1 hover:bg-[#F7F7F7] transition-colors">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-[#258F00] text-white text-xs font-bold">
          {user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
        </span>
        <span className="hidden sm:block text-sm font-semibold max-w-[160px] truncate">{user.name}</span>
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-60">
      <DropdownMenuLabel>
        <div className="text-sm">{user.name}</div>
        <div className="text-xs font-normal text-[#5b6656]">{user.roles.map((r) => r.name).join(", ") || "Sin rol"}</div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild><Link to="/mi-cuenta" data-testid="user-menu-account"><UserRound size={15} className="mr-2" />Mi cuenta</Link></DropdownMenuItem>
      <DropdownMenuItem data-testid="user-menu-logout" onClick={onLogout}><LogOut size={15} className="mr-2" />Cerrar sesión</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { api.get("/company").then((r) => setCompany(r.data.razon_social)).catch(() => {}); }, []);

  if (user === undefined) return <div className="grid min-h-screen place-items-center text-[#5b6656]" data-testid="app-loading">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.must_change_password && location.pathname !== "/mi-cuenta") return <Navigate to="/mi-cuenta" replace />;

  const onLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#F7F7F7]">
      <aside className="hidden lg:block w-[272px] shrink-0 sticky top-0 h-screen border-r border-[#e4e8e1] bg-white px-3 py-5" data-testid="app-sidebar">
        <SidebarContent />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[280px] px-3 py-5" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navegación</SheetTitle>
          <SidebarContent onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[#e4e8e1] bg-white/80 backdrop-blur-xl px-4 md:px-8 h-16">
          <button data-testid="mobile-menu-btn" className="lg:hidden grid h-10 w-10 place-items-center rounded-lg hover:bg-[#F7F7F7]" onClick={() => setOpen(true)}><Menu size={20} /></button>
          <div className="text-xs uppercase tracking-[0.16em] text-[#5b6656] truncate" data-testid="company-name">{company || "S&C"}</div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <UserMenu user={user} onLogout={onLogout} />
          </div>
        </header>
        <main className="px-4 py-6 md:px-8 md:py-10 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
